import { utils as RGLUtils } from 'react-grid-layout';
import { QueryClient } from 'react-query';

import { Props } from 'store/configurationsStore';

import { getReport } from 'api/endPoints/getReport';

import { BreakPointMap, LayoutItem, Report, WidgetSizes } from 'components/Dashboards/Reporting/reporting.types';
import { COMPONENTS_SIZES } from 'components/Dashboards/Reporting/reportingConfig';

/**
 * Given a layout, compact it:
 * - Walks down each y and removes gaps (or x if horizontal)
 * - Prevents widget overlap
 * - compactType = 'horizontal' is RGL's default stacking behavior
 * Clone first to avoid mutating the original.
 */
export const compactSingleLayout = (
    originalLayout: LayoutItem[],
    cols: number,
    compactType: 'vertical' | 'horizontal' | null = 'horizontal', // default matches RGL
): LayoutItem[] => {
    const clonedLayout = originalLayout.map((item) => ({ ...item }));
    return RGLUtils.compact(clonedLayout, compactType, cols);
};

/**
 * Returns bottom available Y position where a new widget can be placed at the bottom of the layout with no overlap.
 * It returns the largest `(y + h)`, meaning the first free row after the bottom edge of the lowest widget.
 *
 * Example:
 *   layout:
 *     { y:0, h:3 }  → occupies rows 0,1,2  → bottom = 3
 *     { y:3, h:2 }  → occupies rows 3,4    → bottom = 5
 *
 *   Result = 5  ← new widget placed at y = 5 (first free row)
 */
export const getBottomFreeYPosition = (gridLayout: BreakPointMap): number => {
    //  breakPoints has the same layout so we'll use the large one and work with it
    const layout = gridLayout.lg ?? [];
    if (layout.length === 0) return 0;

    return layout.reduce((maxBottom, item) => {
        // `itemBottom` = the row just after the bottom of this widget
        const itemBottom = Number(item.y) + Number(item.h);

        // Track the lowest occupied point in the layout
        return Math.max(maxBottom, itemBottom);
    }, 0);
};

export const addLayoutItemToGridLayout = (gridLayout: BreakPointMap, newLayoutItem: LayoutItem): BreakPointMap => {
    return {
        lg: [...gridLayout['lg'], newLayoutItem],
        md: [...gridLayout['md'], newLayoutItem],
        sm: [...gridLayout['sm'], newLayoutItem],
        xs: [...gridLayout['xs'], newLayoutItem],
    };
};

/**
 * Create a new grid layout item for a widget based on componentKey.
 *
 *  - Retrieves the initial width and height for the widget from `componentsSizesMap` based on provided `componentKey`
 *  - Constructs a new `LayoutItem` positioned at `x = 0` and the given `yAxisLocation`.
 *  - Ensures each widget has a unique layout definition with its ID and size.
 *
 * @returns A new `LayoutItem` ready to be inserted into the grid layout.
 */
const createLayoutItem = (
    componentKey: string,
    widgetId: string,
    yAxisLocation: number,
    configurations: Props,
): LayoutItem => {
    const initialStringSize = configurations[componentKey]?.defaultSize;
    const widgetSizeObject = widgetSizesAvailableOptions[initialStringSize];
    if (!widgetSizeObject.w || !widgetSizeObject.h) {
        throw new Error(`Unable to create layout item for componentKey: ${componentKey}. Missing width or height.`);
    }

    const isComponentResizable = configurations[componentKey].isResizable;
    const baseLayout = {
        i: widgetId.toString(),
        x: 0,
        y: yAxisLocation,
        w: widgetSizeObject.w,
        h: widgetSizeObject.h,
    };
    if (isComponentResizable) {
        const componentSize = configurations[componentKey];
        return {
            ...baseLayout,
            minW: componentSize.minW,
            maxW: componentSize.maxW,
            minH: componentSize.minH,
            maxH: componentSize.maxH,
            isResizable: true,
        };
    } else {
        return baseLayout;
    }
};

const IsSameLayoutItem = (a: LayoutItem, b: LayoutItem): boolean => {
    return a.i === b.i && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
};

export const sameLayoutArray = (a: LayoutItem[] = [], b: LayoutItem[] = []): boolean => {
    if (a.length !== b.length) return false;
    return a.every((item, idx) => {
        const other = b[idx];
        return !!other && IsSameLayoutItem(item, other);
    });
};

export const getNewGridLayoutFromSingleLayout = (layout: LayoutItem[]): BreakPointMap => {
    return {
        lg: layout,
        md: layout,
        sm: layout,
        xs: layout,
    };
};

// TODO: change function to work with the breakpoints according to the breakpoints defined type
export const getInitialLayout = (): BreakPointMap => {
    return {
        lg: [],
        xs: [],
        sm: [],
        md: [],
    };
};

export const widgetSizesAvailableOptions = {
    small: { w: 1, h: 3 },
    smallLong: { w: 1, h: 6 },
    mediumShort: { w: 2, h: 3 },
    medium: { w: 2, h: 6 },
    largeShort: { w: 3, h: 3 },
    large: { w: 3, h: 6 },
    extraLarge: { w: 3, h: 9 },
    custom: 'custom',
};

export function getSizeByDimensions({ w, h }: { w: number; h: number }): WidgetSizes | undefined {
    return (Object.keys(COMPONENTS_SIZES) as WidgetSizes[]).find((sizeKey) => {
        const size = COMPONENTS_SIZES[sizeKey];
        return size.w === w && size.h === h;
    });
}

// Half-open interval overlap: [aStart, aEnd) vs [bStart, bEnd)
const rangesOverlap = (aStart: number, aEnd: number, bStart: number, bEnd: number) => aStart < bEnd && bStart < aEnd;

//Push down any items that overlap horizontally with [gapX, gapX+gapW) at/under startY by deltaY
const insertVerticalGapInColumns = (
    layout: LayoutItem[],
    gapX: number,
    gapW: number,
    startY: number,
    deltaY: number,
): LayoutItem[] => {
    const x1 = gapX;
    const x2 = gapX + gapW;

    // Clone to avoid mutating original array
    const layoutCopy = layout.map((it) => ({ ...it }));

    for (const item of layoutCopy) {
        const itX1 = Number(item.x);
        const itX2 = itX1 + Number(item.w);
        const overlapsX = rangesOverlap(x1, x2, itX1, itX2);
        if (!overlapsX) continue;

        // Shift any item that starts at/under the insertion row
        if (Number(item.y) >= startY) {
            item.y = Number(item.y) + deltaY;
        }
    }

    return layoutCopy;
};

// When getting Report from BE the tags interface is { id: number, name: string} ,
// BUT! when we updates Report and send Payload to the BE we need to send Tags the interface is number[]
const convertTagsForSetReportAPI = (tags: Report['tags'] | number[]): number[] => {
    if (!Array.isArray(tags) || tags.length === 0) {
        return [];
    }

    // Already numbers? Return as-is.
    if (typeof tags[0] === 'number') {
        return tags as number[];
    }

    // Narrow types: from here on tags = { id, name }[]
    return (tags as Report['tags']).map((tag) => tag.id);
};

/**
 * Prepares a Report object for the SetReport API request.
 *
 * 1. Removes `accessList` (BE cannot receive this field).
 * 2. Converts `tags` from `{ id, name }[]` to `number[]` — or preserves `number[]` if already converted.
 * 3. Uses `newLayout` if provided, otherwise falls back to `report.gridLayout` and ensures it is a string (stringifies when needed).
 *
 * @returns A clean, API-ready Report payload.
 */
export const prepareReportPayloadForSetReportAPI = (report: Report, newLayout?: BreakPointMap) => {
    // If newLayout passed → stringify it
    // Else use report.gridLayout (already stringified)
    const finalLayout = newLayout ?? report.gridLayout;

    return {
        ...report,
        tags: convertTagsForSetReportAPI(report.tags),
        gridLayout: finalLayout,
    };
};

/**
 * Prepare the payload to persist a report gridLayout after adding a new widget.
 *
 * Behavior:
 * - If `insertAtTop` is true → insert the new widget at the top, pushing all others down.
 * - Else if `sourceWidgetId` is provided AND found in the layout → insert the new widget directly under it.
 * - Otherwise (not provided or not found) → append the new widget at the bottom of the layout.
 *
 */
export const preparePayloadForReport = async (
    queryClient: QueryClient,
    reportId: number,
    newWidgetId: number,
    configurations: Props,
    sourceWidgetId?: number,
    insertAtTop?: boolean,
) => {
    const report: Report = await queryClient.fetchQuery({
        queryKey: ['getReport', { id: reportId }],
        queryFn: () => getReport({ id: reportId }),
    });

    const reportGridLayout: BreakPointMap = report.gridLayout ?? {};
    const layout: LayoutItem[] = (reportGridLayout.lg ?? []).map((it) => ({ ...it }));
    const componentKey = report.components.find((w) => w.id === newWidgetId)?.componentKey ?? '';

    let newReportGridLayout: BreakPointMap;

    const foundSourceWidgetLayoutItem =
        typeof sourceWidgetId === 'number' ? layout.find((li) => li.i === String(sourceWidgetId)) : undefined;

    if (insertAtTop) {
        // Insert at the top, pushing all existing widgets down
        const newLayoutItem: LayoutItem = createLayoutItem(componentKey, String(newWidgetId), 0, configurations);
        const { x, w, h } = newLayoutItem;

        // Push all existing items down by the height of the new widget
        const shiftedLayout = insertVerticalGapInColumns(layout, x, w, 0, h);
        const updatedLayoutWithNewItem = [...shiftedLayout, newLayoutItem];

        newReportGridLayout = getNewGridLayoutFromSingleLayout(updatedLayoutWithNewItem);
    } else if (foundSourceWidgetLayoutItem) {
        // If found → add under the source
        const yAxisLocation = Number(foundSourceWidgetLayoutItem.y) + Number(foundSourceWidgetLayoutItem.h);
        const newLayoutItem: LayoutItem = createLayoutItem(
            componentKey,
            String(newWidgetId),
            yAxisLocation,
            configurations,
        );
        const { x, w, y, h } = newLayoutItem;

        const shiftedLayout = insertVerticalGapInColumns(layout, x, w, y, h);
        const updatedLayoutWithNewItem = [...shiftedLayout, newLayoutItem];

        newReportGridLayout = getNewGridLayoutFromSingleLayout(updatedLayoutWithNewItem);
    } else {
        // Fallback → add to bottom
        const bottomFreeYPosition = getBottomFreeYPosition(reportGridLayout);
        const newLayoutItem: LayoutItem = createLayoutItem(
            componentKey,
            String(newWidgetId),
            bottomFreeYPosition,
            configurations,
        );

        newReportGridLayout = addLayoutItemToGridLayout(reportGridLayout, newLayoutItem);
    }

    return prepareReportPayloadForSetReportAPI(report, newReportGridLayout);
};
